import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError } from './api/client.js';
import { queryKeys } from './api/queries.js';
import { App } from './App.js';
import './styles/index.css';

/**
 * Einstiegspunkt.
 *
 * Nur ein Wiederholungsversuch: Die API antwortet lokal entweder oder sie läuft
 * nicht, und eine lange Kette würde die Fehlermeldung nur verzögern.
 *
 * `refetchOnWindowFocus` ist bewusst aktiv. Importe laufen oft im Terminal
 * (`npm run backfill`); beim Zurückwechseln in den Browser sollen die frischen
 * Kurse erscheinen, ohne dass die Seite neu geladen werden muss.
 */

const isUnauthorized = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

/**
 * Eine abgelaufene Sitzung führt zurück zur Anmeldung.
 *
 * Die Sitzung liegt in einem Cookie und kann jederzeit ablaufen — mitten in der
 * Arbeit, ohne dass etwas passiert wäre. Ohne diese Behandlung stünde dann eine
 * Fehlermeldung auf dem Schirm, während die Anwendung weiter so täte, als wäre
 * jemand angemeldet.
 *
 * Statt hier umzuleiten wird nur der Sitzungseintrag verworfen: Die Abfrage läuft
 * daraufhin erneut, meldet `null`, und die Anwendung zeigt von selbst die
 * Anmeldeseite. Der Weg dorthin ist damit derselbe wie beim ersten Aufruf und
 * nicht ein zweiter, der eigens richtig bleiben müsste.
 */
const handleError = (error: unknown): void => {
  if (!isUnauthorized(error)) return;
  void queryClient.invalidateQueries({ queryKey: queryKeys.session });
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleError }),
  mutationCache: new MutationCache({ onError: handleError }),
  defaultOptions: {
    queries: {
      /* Ein 401 wird durch Wiederholen nicht besser — er wird nur später sichtbar. */
      retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 1,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root-Element #root nicht gefunden');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
